/**
 * Groups opt-in features into the per-BU sections the list screen renders.
 *
 * @module Pages/BUs/OptInFeatures
 */

import type { OptInFeature } from '../Types/OptInFeatures.types';
import { compareTenantSlugs } from '@/Shared/brand/tenantOrder';

/** One BU's section of opt-in features. */
export interface TenantOptInFeatureGroup {
  tenantSlug: string;
  features: OptInFeature[];
  /** e.g. "3 funcionalidades". */
  countLabel: string;
  /** e.g. "2 ativas". */
  activeLabel: string;
}

/**
 * Groups by tenant slug, tenants in the house order and features inside each one by key, so the
 * list does not reshuffle when a kill switch is flipped.
 */
export function groupFeaturesByTenant(features: OptInFeature[]): TenantOptInFeatureGroup[] {
  const byTenant = new Map<string, OptInFeature[]>();

  for (const feature of features) {
    const existing = byTenant.get(feature.tenantSlug);
    if (existing) {
      existing.push(feature);
    } else {
      byTenant.set(feature.tenantSlug, [feature]);
    }
  }

  return [...byTenant.entries()]
    .sort(([a], [b]) => compareTenantSlugs(a, b))
    .map(([tenantSlug, tenantFeatures]) => {
      const ordered = [...tenantFeatures].sort((a, b) => a.key.localeCompare(b.key));
      // A deleted feature reaches nobody, whatever its `isActive` says.
      const activeCount = ordered.filter(feature => feature.isActive && !feature.deletedAt).length;
      const deletedCount = ordered.filter(feature => feature.deletedAt).length;

      return {
        tenantSlug,
        features: ordered,
        countLabel: `${ordered.length} ${ordered.length === 1 ? 'funcionalidade' : 'funcionalidades'}`,
        /*
          Deleted rows are counted apart instead of folded into "ativas": they are in this list
          only because they are holding a key, and counting them as running would be the lie the
          screen exists to stop telling.
        */
        activeLabel:
          deletedCount > 0
            ? `${activeCount} ${activeCount === 1 ? 'ativa' : 'ativas'} · ${deletedCount} excluída${deletedCount === 1 ? '' : 's'}`
            : `${activeCount} ${activeCount === 1 ? 'ativa' : 'ativas'}`,
      };
    });
}
